/**
 * Phone RECOMMENDATIONS (DESIGN-CHANGELOG 2026-09-03 "RECOMMENDATIONS: the
 * fifth door"; `design/GT Mobile Mine.dc.html:177-259`). AA phase 3.
 *
 * The desktop page stacked: the sticky bar (`◂ MY GAMES` · RECOMMENDATIONS,
 * no coin), two 40px segment cells, then one card per row — art + byline +
 * title, the note, a 44px button row. Dismissed TO YOU rows dim and wear the
 * NOT FOR ME chip under the title. REVOKE arms in place.
 */

import type { GameId } from '../lib/types.ts';
import { CoverArt } from '../components/CoverArt.tsx';
import { PageBar } from '../components/PageBar.tsx';
import { useArt, useQueueArt } from '../components/useArt.ts';
import { useQd } from '../store/index.ts';
import { recsVM } from '../store/selectors.ts';
import type { RecInRow, RecOutRow } from '../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

function Av({ ini, hueBg, hasAv, avBg, size }: { ini: string; hueBg: string; hasAv: boolean; avBg: string; size: number }): JSX.Element {
  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: size,
        flex: 'none',
        background: hueBg,
        display: 'grid',
        placeItems: 'center',
        fontFamily: PIXEL,
        fontSize: size * 0.3,
        color: 'var(--gt-ink)',
        overflow: 'hidden',
      }}
    >
      {ini}
      {hasAv ? <div style={{ position: 'absolute', inset: 0, background: avBg }} /> : null}
    </div>
  );
}

function Head({ gid, ini, fall, cands, by, title, chip, from }: {
  gid: GameId;
  ini: string;
  fall: string;
  cands: RecInRow['hdrCands'];
  by: string;
  title: string;
  chip?: string;
  from?: { ini: string; hueBg: string; hasAv: boolean; avBg: string };
}): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const art = useArt(gid + 'w', cands);
  return (
    <div style={{ display: 'flex', gap: 11, alignItems: 'center' }}>
      <div
        onClick={() => openGame(gid)}
        style={{ position: 'relative', width: 104, height: 49, flex: 'none', background: fall, overflow: 'hidden', border: '1px solid var(--gt-line)', cursor: 'pointer' }}
      >
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={10} ini={ini} hasCover={art.hasCover} cover={art.cover} />
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          {from ? <Av ini={from.ini} hueBg={from.hueBg} hasAv={from.hasAv} avBg={from.avBg} size={16} /> : null}
          <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.06em', color: 'var(--gt-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{by}</span>
        </div>
        <span onClick={() => openGame(gid)} style={{ fontFamily: 'var(--gt-font-sans)', fontWeight: 600, fontSize: '13.5px', color: 'var(--gt-text)', cursor: 'pointer' }}>
          {title}
        </span>
        {chip ? (
          <span style={{ alignSelf: 'flex-start', fontFamily: MONO, fontSize: 7.5, letterSpacing: '.09em', color: 'var(--acc2)', border: '1px solid var(--acc2)', padding: '2px 5px' }}>{chip}</span>
        ) : null}
      </div>
    </div>
  );
}

const BTN = {
  flex: 1,
  height: 44,
  display: 'grid',
  placeItems: 'center',
  fontFamily: MONO,
  fontSize: 9.5,
  letterSpacing: '.07em',
  cursor: 'pointer',
  boxSizing: 'border-box',
  position: 'relative',
  overflow: 'hidden',
} as const;

function InCard({ r }: { r: RecInRow }): JSX.Element {
  const st = useQd();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 0', borderBottom: '1px solid var(--gt-line-0)', opacity: r.op, transition: 'opacity .12s ease' }}>
      <Head
        gid={r.gid}
        ini={r.ini}
        fall={r.fall}
        cands={r.hdrCands}
        by={r.by}
        title={r.n}
        from={{ ini: r.fromIni, hueBg: r.fromHueBg, hasAv: r.fromHasAv, avBg: r.fromAvBg }}
        {...(r.dismissed ? { chip: 'NOT FOR ME' } : {})}
      />
      <span style={{ fontFamily: 'var(--gt-font-sans)', fontSize: 14, lineHeight: 1.5, color: 'var(--gt-muted)', textWrap: 'pretty' }}>{r.note}</span>
      <div style={{ display: 'flex', gap: 8 }}>
        {r.added ? (
          <div style={{ ...BTN, color: 'var(--acc)', cursor: 'default' }}>✓ ON YOUR BACKLOG</div>
        ) : (
          <div onClick={() => void st.sendAdd(r.id)} style={{ ...BTN, color: 'var(--acc)', border: '1px solid var(--acc)', background: 'var(--accSoft)' }}>
            ADD TO BACKLOG ▸
          </div>
        )}
        {!r.added ? (
          <div onClick={() => void st.sendSet(r.id, r.dismissed ? '' : 'x')} style={{ ...BTN, color: 'var(--gt-text-5)', border: '1px solid var(--gt-border-2)' }}>
            {r.dismissL}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function OutCard({ r }: { r: RecOutRow }): JSX.Element {
  const st = useQd();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 0', borderBottom: '1px solid var(--gt-line-0)' }}>
      <Head gid={r.gid} ini={r.ini} fall={r.fall} cands={r.hdrCands} by={r.by} title={r.n} />
      <span style={{ fontFamily: 'var(--gt-font-sans)', fontSize: 14, lineHeight: 1.5, color: 'var(--gt-muted)', textWrap: 'pretty' }}>“{r.note}”</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {r.who.map((w) => (
          <div key={w.key} style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--gt-line)', padding: '4px 7px 4px 4px' }}>
            <Av ini={w.ini} hueBg={w.hueBg} hasAv={w.hasAv} avBg={w.avBg} size={18} />
            <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.05em', color: 'var(--gt-text-4)' }}>{w.name}</span>
            <span style={{ fontFamily: MONO, fontSize: 7.5, letterSpacing: '.08em', color: w.chipClr, border: '1px ' + w.chipBs + ' ' + w.chipClr, padding: '2px 4px' }}>{w.chip}</span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div onClick={() => st.sdOpenEdit(r.gid)} style={{ ...BTN, color: 'var(--acc)', border: '1px solid var(--acc)', background: 'var(--accSoft)' }}>
          EDIT ▸
        </div>
        <div
          onClick={() => (r.armed ? void st.sdRevoke(r.gid) : st.rcArmSet(r.gid))}
          data-arm={'rc' + r.gid}
          style={{ ...BTN, color: r.armed ? 'var(--acc2)' : 'var(--gt-text-5)', border: '1px solid ' + (r.armed ? 'var(--acc2)' : 'var(--gt-border-2)') }}
        >
          {r.armed ? (
            <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '100%', background: 'var(--acc2)', opacity: 0.22, animation: 'gt-armdrain 5s linear forwards' }} />
          ) : null}
          <span style={{ gridArea: '1/1', position: 'relative', visibility: r.armed ? 'hidden' : 'visible' }}>REVOKE</span>
          <span style={{ gridArea: '1/1', position: 'relative', visibility: r.armed ? 'visible' : 'hidden' }}>SURE?</span>
        </div>
      </div>
    </div>
  );
}

export default function Recs(): JSX.Element {
  const s = useQd();
  const v = recsVM(s);

  // The ledger fetches its own art. A recommendation is a game somebody ELSE
  // picked, so it is routinely on no surface that reaches `/api/steam/items` —
  // and without a fetched asset record a modern appid has no art at all (the
  // note in `screens/Mine.tsx`). Both tabs, not just the open one: switching
  // tabs must not be the thing that finally loads the covers.
  useQueueArt([...v.rcIn.map((r) => r.gid), ...v.rcOut.map((r) => r.gid)]);

  return (
    <div data-screen-label="Recommendations" style={{ display: 'flex', flexDirection: 'column', flex: 1, paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <PageBar title="RECOMMENDATIONS" />
      <div style={{ display: 'flex', fontFamily: MONO, fontSize: 10, padding: '12px 14px 0' }}>
        {v.tabs.map((t) => (
          <div
            key={t.key}
            onClick={() => s.setRcTab(t.key)}
            style={{ flex: 1, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid ' + t.bd, color: t.clr, background: t.bg, marginLeft: -1, position: 'relative', zIndex: t.z, cursor: 'pointer', boxSizing: 'border-box' }}
          >
            {t.l}
          </div>
        ))}
      </div>
      <div style={{ padding: '4px 14px 26px' }}>
        {v.tab === 'in' ? v.rcIn.map((r) => <InCard key={r.key} r={r} />) : v.rcOut.map((r) => <OutCard key={r.key} r={r} />)}
        {v.inEmpty ? (
          <div style={{ padding: '30px 0', textAlign: 'center', fontFamily: MONO, fontSize: 10, letterSpacing: '.06em', color: 'var(--gt-muted)' }}>NOBODY HAS RECOMMENDED YOU A GAME YET</div>
        ) : null}
        {v.outEmpty ? (
          <div style={{ padding: '30px 0', textAlign: 'center', fontFamily: MONO, fontSize: 10, letterSpacing: '.06em', lineHeight: 2, color: 'var(--gt-muted)' }}>
            YOU HAVEN'T RECOMMENDED ANYTHING YET
            <br />
            RECOMMEND ▸ LIVES ON EVERY GAME'S DETAIL PAGE
          </div>
        ) : null}
      </div>
    </div>
  );
}
